/**
 * RoleFormPageV2 — redesigned create/edit form for a role.
 *
 * Sections:
 *   01 Basic — role_name + role_description
 *   02..N Permissions — one FormSection per permission_category,
 *                       with a "Select all" toggle on the section's
 *                       right slot and a grid of checkbox-chip rows.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOneRole,
  saveRole,
} from '../../../../actions/rolesActions';
import { fetchAllPermissions } from '../../../../actions/permissionsActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { Button, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  TextInput,
  Textarea,
} from '../../../redesign/edit';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

/* ─── Formatters ───────────────────────────────────────────────── */

function formatDisplayName(name) {
  return (name || '')
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/* ─── Permission toggle chip ───────────────────────────────────── */

function PermissionChip({ label, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn('flex items-center text-left transition-all duration-150')}
      style={{
        gap: 10,
        padding: '8px 12px',
        background: selected ? '#75A2931A' : '#FAF4EA',
        border: `1px solid ${selected ? '#75A293' : '#E8E0CF'}`,
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
        width: '100%',
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = '#75A293';
        e.currentTarget.style.background = '#FAF7F1';
      }}
      onMouseLeave={(e) => {
        if (selected) return;
        e.currentTarget.style.borderColor = '#E8E0CF';
        e.currentTarget.style.background = '#FAF4EA';
      }}
    >
      <span
        aria-hidden
        className="grid place-items-center shrink-0"
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          background: selected ? '#75A293' : 'transparent',
          border: `1.5px solid ${selected ? '#75A293' : '#C8BFA8'}`,
          color: '#fff',
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {selected && '✓'}
      </span>
      <span
        className="flex-1 min-w-0 truncate text-navy"
        style={{ fontSize: 13 }}
      >
        {label}
      </span>
    </button>
  );
}

/* ─── Main ─────────────────────────────────────────────────────── */

export default function RoleFormPageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [role, setRole] = useState({
    role_id: 0,
    role_name: '',
    role_description: '',
    permissions: [],
  });

  const loading = useSelector((s) => s.roles.loading);
  const roleStore = useSelector((s) => s.roles.role);
  const permissions = useSelector((s) => s.permissions.permissions);

  useEffect(() => {
    dispatch(fetchAllPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (id) dispatch(fetchOneRole(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (!id) {
      setRole({
        role_id: 0,
        role_name: '',
        role_description: '',
        permissions: [],
      });
    } else if (roleStore) {
      const roleData = roleStore.role || roleStore;
      const rolePermissions = roleStore.permissions || [];
      setRole({
        ...roleData,
        permissions: rolePermissions.map((p) => p.permission_id),
      });
    }
  }, [roleStore, id]);

  const handleChange = (e) => {
    setRole((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const togglePermission = (permissionId) => {
    setRole((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((pid) => pid !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const toggleCategory = (category) => {
    const categoryIds = (permissions || [])
      .filter((p) => p.permission_category === category)
      .map((p) => p.permission_id);
    const allSelected = categoryIds.every((cid) =>
      role.permissions.includes(cid),
    );
    setRole((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((pid) => !categoryIds.includes(pid))
        : Array.from(new Set([...prev.permissions, ...categoryIds])),
    }));
  };

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    dispatch(saveRole({ ...role }, () => navigate('/roles')));
  };

  /* ─── Group permissions by category for rendering ──────────── */
  const grouped = (() => {
    const g = {};
    (permissions || []).forEach((p) => {
      if (!g[p.permission_category]) g[p.permission_category] = [];
      g[p.permission_category].push(p);
    });
    return g;
  })();

  const eyebrow = isEdit ? 'Editing role' : 'New role';
  const title = isEdit ? role.role_name || `Role #${id}` : 'New role';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !role.role_name && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>role_id · <strong className="text-navy">{id}</strong></>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/roles" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="role-form"
              leadingIcon={SaveIconSvg}
              disabled={loading}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <form
          id="role-form"
          onSubmit={handleSave}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          <FormSection title="Basic" num="01">
            <FieldBlock label="Role name" required htmlFor="f-role-name">
              <TextInput
                id="f-role-name"
                name="role_name"
                value={role.role_name}
                onChange={handleChange}
                autoFocus
                required
              />
            </FieldBlock>
            <FieldBlock
              label="Description"
              hint="Optional — useful when assigning the role"
              htmlFor="f-role-description"
              noMargin
            >
              <Textarea
                id="f-role-description"
                name="role_description"
                value={role.role_description}
                onChange={handleChange}
                rows={3}
              />
            </FieldBlock>
          </FormSection>

          {Object.entries(grouped).map(([category, categoryPerms], idx) => {
            const allSelected = categoryPerms.every((p) =>
              role.permissions.includes(p.permission_id),
            );
            const selectedCount = categoryPerms.filter((p) =>
              role.permissions.includes(p.permission_id),
            ).length;
            const num = String(idx + 2).padStart(2, '0');
            return (
              <FormSection
                key={category}
                title={`Permissions · ${formatDisplayName(category)}`}
                num={num}
                right={
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <span
                      className="text-text-muted"
                      style={{ fontSize: 11.5 }}
                    >
                      {selectedCount} / {categoryPerms.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="font-condensed font-bold uppercase"
                      style={{
                        fontSize: 10,
                        letterSpacing: '0.08em',
                        padding: '3px 9px',
                        borderRadius: 999,
                        border: '1px solid #75A293',
                        background: allSelected ? '#75A293' : 'transparent',
                        color: allSelected ? '#fff' : '#5C7E72',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                }
              >
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                  style={{ gap: 8 }}
                >
                  {categoryPerms.map((p) => (
                    <PermissionChip
                      key={p.permission_id}
                      label={formatDisplayName(p.permission_name)}
                      selected={role.permissions.includes(p.permission_id)}
                      onToggle={() => togglePermission(p.permission_id)}
                    />
                  ))}
                </div>
              </FormSection>
            );
          })}
        </form>
      </div>
    </div>
  );
}
